import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Row } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { SearchInput } from '../../../components/SearchInput';
import { CheckboxContainer, StyledCheckboxItem } from '../style';
import { StyledRow, StyledSelectUsersModal } from './style';

interface UserList {
  email?: string;
  label: string;
  value: string | number;
  isChecked: boolean;
  type: 'New' | 'Old';
  enabled?: boolean;
}

interface Props {
  visible: boolean;
  userList: UserList[];
  selectedList: UserList[];
  onSelect: Function;
  onCloseModal: Function;
}

const StyledSearchInput = styled(props => <SearchInput {...props} />)``;
const SelectUserModal: FC<Props> = ({
  visible,
  userList,
  selectedList,
  onSelect,
  onCloseModal,
}) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [modalUserList, setModalUserList] = useState<UserList[]>([]);
  const onSearch = useCallback((keyword: string) => {
    setSearchTerm(keyword);
  }, []);

  useEffect(() => {
    setModalUserList(userList.filter(user => user.enabled === true));
  }, [userList]);

  const onCheckboxChange = useCallback(
    (e: CheckboxChangeEvent) => {
      const modalUserListData: UserList[] = [...modalUserList];
      const modalUserListDataIndex = modalUserListData.findIndex(
        user => user.value === e.target.value,
      );
      if (modalUserListDataIndex !== -1) {
        modalUserListData[modalUserListDataIndex].isChecked = e.target.checked;
        setModalUserList([...modalUserListData]);
      }
    },
    [modalUserList],
  );

  const onClose = () => {
    const selectedUsersId = selectedList.map((user: UserList) => user.value);
    const modalUserListData: UserList[] = [...modalUserList];
    const updatedData = modalUserListData.map(user => {
      if (!selectedUsersId.includes(user.value)) {
        user.isChecked = false;
        return user;
      }
      return user;
    });
    setModalUserList([...updatedData]);
    onCloseModal();
  };

  const onSelectBtn = () => {
    const selected = modalUserList.filter(
      (user: UserList) => user.isChecked === true,
    );
    onSelect([
      ...selected,
      ...userList.filter(
        user => user.enabled === false && user.isChecked === true,
      ),
    ]);
  };

  const renderUserList = useCallback(() => {
    if (modalUserList.length === 0 && searchTerm.length === 0) {
      return (
        <StyledCheckboxItem key="empty" className="emptyuserblock">
          <FormattedMessage id="UserGroup.list.placeholder" />
        </StyledCheckboxItem>
      );
    } else if (modalUserList.length > 0 && searchTerm.length === 0) {
      return modalUserList.map((user: any, index: number) => (
        <StyledCheckboxItem key={index}>
          <Checkbox
            checked={user.isChecked}
            onChange={e => onCheckboxChange(e)}
            value={user.value}
            data-testid={`select-user-${index}`}
          >
            {user.label}&nbsp;
            {user.email && `| ${user.email}`}
          </Checkbox>
        </StyledCheckboxItem>
      ));
    } else {
      const filterData = modalUserList.filter(user => {
        if (
          user.label
            .toLocaleLowerCase()
            .indexOf(searchTerm.toLocaleLowerCase()) !== -1
        ) {
          return true;
        }
        return false;
      });

      if (filterData.length > 0) {
        return filterData.map((user: any, index: number) => (
          <StyledCheckboxItem key={index}>
            <Checkbox
              checked={user.isChecked}
              onChange={e => onCheckboxChange(e)}
              value={user.value}
              data-testid={`select-user-${index}`}
            >
              {user.label}&nbsp;
              {user.email && `| ${user.email}`}
            </Checkbox>
          </StyledCheckboxItem>
        ));
      } else {
        return (
          <StyledCheckboxItem key="no-data">
            <FormattedMessage id="UserGroup.user.noDataMatch" />
          </StyledCheckboxItem>
        );
      }
    }
  }, [onCheckboxChange, searchTerm, modalUserList]);

  return (
    <StyledSelectUsersModal
      visible={visible}
      closable={false}
      footer={null}
      width={568}
      data-testid="select-users-modal"
    >
      <h3>
        <FormattedMessage id="UserGroup.modal.title.selectUsers" />
      </h3>
      <StyledRow>
        <Col span={24}>
          <StyledSearchInput
            placeholder={intl.formatMessage({
              id: 'UserGroup.modal.placeholder.selectUsers',
            })}
            testId="select-users-modal-search"
            value={searchTerm}
            data-testid="search-files-folders"
            prefix={<SearchOutlined />}
            onChange={onSearch}
            hideClearAll
          />
        </Col>
      </StyledRow>
      <Row>
        <Col span={24}>
          <CheckboxContainer>{renderUserList()}</CheckboxContainer>
        </Col>
      </Row>
      <Row>
        <Col
          style={{
            marginTop: '30px',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
          }}
          span={24}
        >
          <Button
            type="default"
            shape="round"
            block
            style={{
              width: '200px',
            }}
            onClick={onClose}
            data-testid="select-cancel-submit"
          >
            <FormattedMessage id="Global.cancel" />
          </Button>
          <Button
            type="primary"
            shape="round"
            block
            style={{
              width: '200px',
            }}
            onClick={onSelectBtn}
            data-testid="select-user-submit"
          >
            <FormattedMessage id="UserGroup.modal.btn.select" />
          </Button>
        </Col>
      </Row>
    </StyledSelectUsersModal>
  );
};

export default SelectUserModal;
